import React, { useMemo } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { getAlertColorsAndIcon, alertPrioritySort, getAlertTimestamp, formatAlertDescription, isItemActivelySilenced } from '../../../utils/alertUtils';
import { formatTime, formatDateWithFullMonth } from '../../../utils/dateUtils';
import alertStyles from '../../../components/Alerts/Alert.module.css';
import cardDetailsStyles from '../../../views/patient/CardDetails/CardDetails.module.css';
import PatientDetailDropdownCard from '../../../components/PatientDetailDropdownCard';
import { SmallGearIcon } from '../../../components/icons/Icons';

const InpatientAlarmsListModal = ({ show, onHide, alerts, patientName, onAcknowledgeAll, onAlarmSettings }) => {
    const { dateFormat, unitType } = useSelector((state) => state.userInfo);

    const formatCompletedDate = (item) => {
        const timestamp = getAlertTimestamp(item);
        let formattedTime = formatTime(timestamp);
        let formattedDate = formatDateWithFullMonth(timestamp, dateFormat);
        return (
            <div style={{ display: "flex", columnGap: "10px", alignItems: "center" }}>
                <span className="body-2" style={{ borderRight: "1px solid #9D9D9E", paddingRight: "10px", fontWeight: "500" }}>{formattedTime}</span>
                <span className="body-2" style={{ fontWeight: "500" }}>{formattedDate}</span>
            </div>
        );
    };

    // Sort alerts by priority
    const sortedAlerts = useMemo(() => {
        return [...alerts].sort(alertPrioritySort);
    }, [alerts]);

    const renderAlertContent = (alert) => {
        const isSilenced = isItemActivelySilenced(alert);
        let { icon, color, background } = getAlertColorsAndIcon(alert);

        return (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', width: '100%' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <div style={{ marginBottom: '4px' }}>
                        <strong>Priority:</strong> {String(alert.alert_priority || 'medium').charAt(0).toUpperCase() + String(alert.alert_priority || 'medium').slice(1)}
                    </div>
                    <div style={{ marginBottom: '8px' }}>
                        <strong>Description:</strong> {formatAlertDescription(alert, unitType)}
                    </div>
                    {isSilenced && alert.alert_silenceTimestamp && (
                        <div style={{ 
                            marginTop: '4px', 
                            padding: '8px', 
                            background: '#f0f8f0', 
                            borderRadius: '4px',
                            border: '1px solid #2A7F38'
                        }}>
                            <div style={{ 
                                display: 'flex', 
                                flexDirection: 'column', 
                                color: '#2A7F38', 
                                fontSize: '14px',
                                fontWeight: '500'
                            }}>
                                <span>Snoozed until {formatTime(alert.alert_silenceTimestamp)}</span>
                                <span style={{ fontSize: '12px', fontWeight: '400' }}>
                                    {formatDateWithFullMonth(alert.alert_silenceTimestamp, dateFormat)}
                                </span>
                            </div>
                        </div>
                    )}
                </div>
                {onAlarmSettings && (
                    <button
                        className={cardDetailsStyles.alarmSettingsButton}
                        onClick={() => onAlarmSettings(alert)}
                    >
                        <SmallGearIcon />
                        Alarm Settings
                    </button>
                )}
            </div>
        );
    };

    return (
        <Modal
            show={show}
            onHide={onHide}
            centered
            size="lg"
            className={alertStyles.alarmOptionsModal}
        >
            <Modal.Header closeButton>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                    <div className="subtitle-1">
                        Alarms for {patientName}
                    </div>
                    {onAcknowledgeAll && sortedAlerts.length > 0 && (
                        <Button
                            variant="primary"
                            onClick={onAcknowledgeAll}
                            style={{
                                background: '#1D5769',
                                border: 'none',
                                fontWeight: '700'
                            }}
                        >
                            Clear All
                        </Button>
                    )}
                </div>
            </Modal.Header>
            <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {sortedAlerts.length === 0 ? (
                    <div style={{ padding: '16px' }}>No active alarms found</div>
                ) : (
                    sortedAlerts.map((alert, index) => {
                        let { icon } = getAlertColorsAndIcon(alert);

                        return (
                            <PatientDetailDropdownCard
                                key={alert.alertId || index}
                                title={alert.alert_display_name ?? alert.alert_name}
                                icon={icon}
                                subtitle2={formatCompletedDate(alert)}
                                cardInfo={renderAlertContent(alert)}
                                isOpen={true}
                                width={'100%'}
                            />
                        );
                    })
                )}
            </Modal.Body>
        </Modal>
    );
};

export default InpatientAlarmsListModal;
